<template>
  <div :id="id" class="switch-btn">
    <input
      v-model="_dataModel"
      :id="`${id}-left`"
      :name="`${id}-left`"
      type="radio"
      :value="1"
      class="switch-radio left"
      :checked="checked === 1"
      :disabled="disabled"
    />
    <input
      v-model="_dataModel"
      :id="`${id}-right`"
      :name="`${id}-right`"
      type="radio"
      :value="0"
      class="switch-radio right"
      :checked="checked === 0"
      :disabled="disabled"
    />
    <div :id="`${id}-slider`" class="slider">
      <label class="switch-label left active" :for="`${id}-left`">{{ activeLabel }}</label>
      <label class="switch-label right inactive" :for="`${id}-right`">{{ inactiveLabel }}</label>
    </div>
  </div>
</template>

<script>
import AppConstants from '../../constants/app.constants'
import CssUtils from '../../_helpers/css.util'
const SWITCH_BUTTON_WIDTH_DEFAULT = 120
export default {
  model: {
    prop: 'dataModel',
    event: 'change'
  },
  props: {
    dataModel: {
      type: Boolean | Number,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    checked: {
      type: Boolean | Number,
      default: false
    },
    id: String,
    activeLabel: {
      type: String,
      default: '無効'
    },
    inactiveLabel: {
      type: String,
      default: '有効'
    }
  },
  mounted() {
    this.setSwitchBtnStyle()
    window.addEventListener('resize', this.setSwitchBtnStyle)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setSwitchBtnStyle)
  },
  computed: {
    _dataModel: {
      get() {
        return this.dataModel
      },
      set(value) {
        this.$emit('change', value)
      }
    }
  },
  methods: {
    /**
     * スイッチボタンのスタイルを設定します。
     */
    setSwitchBtnStyle() {
      CssUtils.setCssVariable(AppConstants.SWITCH_LABEL_WIDTH_VARIABLE_NAME, this.getSwitchLabelWidth())
      CssUtils.setCssVariable(AppConstants.SWITCH_BUTTON_WIDTH_VARIABLE_NAME, this.getSwitchBtnWidth())
    },
    /**
     * スイッチボタンの幅を取得します。
     */
    getSwitchBtnWidth() {
      const slideId = document.getElementById(`${this.id}-slider`)
      if (!slideId) {
        return `${SWITCH_BUTTON_WIDTH_DEFAULT}px`
      }
      return `${slideId.offsetWidth}px`
    },
    /**
     * スイッチボタンのラベル幅を取得します。
     */
    getSwitchLabelWidth() {
      const leftLabel = this.createVirtualLabel(this.activeLabel)
      const rightLabel = this.createVirtualLabel(this.inactiveLabel)
      if (leftLabel > rightLabel) {
        return `${leftLabel}px`
      }
      return `${rightLabel}px`
    },
    /**
     * 仮想ラベルを作成して実際のラベルの幅を取得します。
     * @param {string} labelText - ラベルのテキスト
     */
    createVirtualLabel(labelText) {
      let text = document.createElement('label')
      const parentElm = document.getElementById(this.id) || document.body
      parentElm.appendChild(text)
      text.classList.add('virtual-switch-label')
      text.innerHTML = labelText
      let labelWidth = text.offsetWidth
      parentElm.removeChild(text)
      return labelWidth
    }
  }
}
</script>

<style lang="scss" scoped>
.switch-btn {
  --switch-icon-size: 52px;
  --switch-button-padding: 25px;
  --switch-button-border-space: 8px;
  height: 100%;
  width: var(--switch-button-width);
  .switch-radio {
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    border: 0;
    position: absolute;
    clip: rect(0 0 0 0);
    overflow: hidden;
    &:disabled +.slider .switch-label {
      cursor: default;
    }

    &:checked+.slider .switch-label.inactive {
      opacity: 0;
    }

    &:checked+.slider .switch-label.active {
      opacity: 1;
      z-index: 20;
    }
  }

  .slider {
    user-select: none;
    content: "";
    position: absolute;
    cursor: pointer;
    width: calc(var(--switch-label-width) + var(--switch-icon-size) + var(--switch-button-padding));
    height: 100%;
    border-radius: 70px;
    background-color: #4e66a6;
    border: 1px solid #afafae;
    transition: background-color 300ms ease-in-out;

    &:after {
      $center-switch-icon: 3px;
      content: "";
      position: absolute;
      top: $center-switch-icon;
      left: $center-switch-icon;
      height: calc(100% - (#{$center-switch-icon} * 2));
      border-radius: 50%;
      aspect-ratio: 1 / 1;
      background-color: #ffffff;
      border: 2px solid rgba(78, 102, 166, 0.24);
      transition: transform 300ms ease-in-out;
      background-image: url(../../assets/images/icon_handle.svg);
      background-repeat: no-repeat;
      background-position: center;
      transform: translateX(calc(var(--switch-label-width) + var(--switch-button-padding) - var(--switch-button-border-space)));
    }
  }
  .slider.disabled {
    background-color: rgba(37, 36, 34, 0.08);
    &:after {
      background: #252422;
    }
  }
  .switch-label {
    box-sizing: border-box;
    position: absolute;
    display: flex;
    align-items: center;
    @include responsive-fs(24);
    width: 100%;
    height: 100%;
    padding: 8px;
    color: #ffffff;
    font-weight: bold;
    cursor: pointer;
    top: 0;
    margin-bottom: 0;
    transition: all, 0.4s, ease-out;

    &.active {
      opacity: 0;
      left: 0;
    }

    &.inactive {
      z-index: 2;
      right: 0;
    }
  }

  .switch-label.left {
    justify-content: flex-end;
  }

  .switch-label.right {
    justify-content: flex-start;
  }

  /* checked logic selector */
  .switch-radio.right:checked+.slider {
    background-color: #AFAFAE;
    &:after {
      background-color: #D6D6D6;
      border: 2px solid rgba(78, 102, 166, 0.24);
    }
  }

  /* checked logic selector */
  .switch-radio.right:checked+.slider:after {
    transform: translateX(0);
  }

  /* checked logic selector */
  .switch-radio.left:checked+.switch-label.right:after {
    content: "";
    position: absolute;
    top: -10px;
    right: 0;
    bottom: -10px;
    left: -36px;
    z-index: 2;
  }
}
@include media-breakpoint-default() {
  .switch-btn {
    --switch-icon-size: 40px;
    --switch-button-padding: 20px;
    --switch-button-border-space: 6px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/custom-components/SwitchButton.vue